// Rich demo dataset for the in-memory mock layer. Cross-references between
// entities (a quote points to a buy request, an order points to a quote, a
// conversation to a buyer, etc.) so the UI tells a coherent story to a client.
//
// Mutability: arrays are `let`/`array.push/splice` so POST/PATCH/DELETE
// handlers can update them and subsequent GETs reflect the change. State
// resets on page reload — that's fine for a demo.

import type { Lead } from '@entities/crm';
import type { ConversationView, MessageView } from '@entities/chat';
import type { NotificationView } from '@entities/notification';
import type { OrderView } from '@entities/order';
import type { ProductView } from '@entities/product';
import type { BuyRequest, Quote } from '@entities/rfq';
import type { Category, CategoryKind } from '@entities/taxonomy';
import type { UserBasicInfo } from '@entities/user-directory';
import type { Carrier, ShipmentView } from '@entities/logistics';
import type { MediaLink } from '@entities/media';

/**
 * Build a MediaLink with a stable seeded SVG data-URL so the UI gets a
 * deterministic preview without hitting any network endpoint.
 */
function link(id: string, opts?: { publicUrl?: boolean; contentType?: string }): MediaLink {
  const hue = Array.from(id).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 360, 0);
  const tag = id.slice(-3).toUpperCase();
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'>` +
    `<rect width='80' height='80' fill='hsl(${hue},65%,55%)'/>` +
    `<text x='50%' y='55%' fill='white' font-family='Inter,sans-serif' font-size='12' font-weight='600' text-anchor='middle'>${tag}</text>` +
    `</svg>`;
  return {
    id,
    url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
    contentType: opts?.contentType ?? 'image/svg+xml',
    publicUrl: opts?.publicUrl ?? true,
  };
}

function links(ids: string[], opts?: { publicUrl?: boolean; contentType?: string }): MediaLink[] {
  return ids.map((id) => link(id, opts));
}

// ----- tokens / current user / profile ------------------------------------

export const MOCK_TOKENS = {
  accessToken: 'mock-access-token',
  refreshToken: 'mock-refresh-token',
  expiresIn: 900,
  refreshExpiresIn: 60 * 60 * 24 * 30,
};

const PRODUCER_USER_ID = '00000000-0000-0000-0000-000000000001';

export const MOCK_USER = {
  id: PRODUCER_USER_ID,
  email: 'demo@acme.local',
  phone: null,
  role: 'PRODUCER' as const,
  kycStatus: 'VERIFIED' as const,
};

// Stable UUIDs so cross-refs work cleanly across endpoints.
// PRODUCER and ECO_PRODUCER kinds drive the manufacturer profile selector.
// PRODUCT kind feeds the catalog/buy-request filters.
const CAT = {
  // PRODUCER (production sectors)
  textiles: '11111111-aaaa-1111-1111-000000000001',
  apparel: '11111111-aaaa-1111-1111-000000000002',
  food: '11111111-aaaa-1111-1111-000000000003',
  electronics: '11111111-aaaa-1111-1111-000000000004',
  machinery: '11111111-aaaa-1111-1111-000000000005',
  chemicals: '11111111-aaaa-1111-1111-000000000006',
  metals: '11111111-aaaa-1111-1111-000000000007',
  homeware: '11111111-aaaa-1111-1111-000000000008',

  // ECO_PRODUCER (sustainable production specialisations)
  ecoTextiles: '11111111-bbbb-1111-1111-000000000001',
  ecoFood: '11111111-bbbb-1111-1111-000000000002',
  ecoPackaging: '11111111-bbbb-1111-1111-000000000003',
  ecoEnergy: '11111111-bbbb-1111-1111-000000000004',
  ecoCosmetics: '11111111-bbbb-1111-1111-000000000005',

  // PRODUCT (marketplace product categories)
  productTextiles: '11111111-cccc-1111-1111-000000000001',
  productApparel: '11111111-cccc-1111-1111-000000000002',
  productHomeware: '11111111-cccc-1111-1111-000000000003',
  productElectronics: '11111111-cccc-1111-1111-000000000004',
  productFood: '11111111-cccc-1111-1111-000000000005',
  productMachinery: '11111111-cccc-1111-1111-000000000006',
};

const BUYER = {
  germany: '22222222-aaaa-aaaa-aaaa-000000000001',
  uk: '22222222-aaaa-aaaa-aaaa-000000000002',
  usa: '22222222-aaaa-aaaa-aaaa-000000000003',
  india: '22222222-aaaa-aaaa-aaaa-000000000004',
  uae: '22222222-aaaa-aaaa-aaaa-000000000005',
};

const PROD = {
  ecoCotton: '33333333-1111-1111-1111-000000000001',
  recycledPoly: '33333333-1111-1111-1111-000000000002',
  denimStock: '33333333-1111-1111-1111-000000000003',
  draftBag: '33333333-1111-1111-1111-000000000004',
  pendingTowel: '33333333-1111-1111-1111-000000000005',
  rejectedShirt: '33333333-1111-1111-1111-000000000006',
};

const RFQ = {
  publicCotton: '44444444-1111-1111-1111-000000000001',
  publicLinen: '44444444-1111-1111-1111-000000000002',
  publicHomeware: '44444444-1111-1111-1111-000000000003',
  targetedDenim: '44444444-1111-1111-1111-000000000004',
  closedShirts: '44444444-1111-1111-1111-000000000005',
  expiredTowels: '44444444-1111-1111-1111-000000000006',
};

const QUOTE = {
  activeCotton: '55555555-1111-1111-1111-000000000001',
  activeDenim: '55555555-1111-1111-1111-000000000002',
  acceptedHomeware: '55555555-1111-1111-1111-000000000003',
  withdrawnLinen: '55555555-1111-1111-1111-000000000004',
  activeLinen2: '55555555-1111-1111-1111-000000000005',
};

const ORDER = {
  newShirts: '66666666-1111-1111-1111-000000000001',
  inProcessing: '66666666-1111-1111-1111-000000000002',
  readyToShip: '66666666-1111-1111-1111-000000000003',
  inTransit: '66666666-1111-1111-1111-000000000004',
  delivered: '66666666-1111-1111-1111-000000000005',
};

const CARRIER_IDS = {
  dhl: '77777777-1111-1111-1111-000000000001',
  fedex: '77777777-1111-1111-1111-000000000002',
  maersk: '77777777-1111-1111-1111-000000000003',
};

const CONV = {
  germany: '88888888-1111-1111-1111-000000000001',
  uk: '88888888-1111-1111-1111-000000000002',
  india: '88888888-1111-1111-1111-000000000003',
  uae: '88888888-1111-1111-1111-000000000004',
};

const NOW = '2026-06-22T10:00:00Z';
const DAY = 24 * 60 * 60 * 1000;
const isoDaysAgo = (n: number): string => new Date(new Date(NOW).getTime() - n * DAY).toISOString();

// ----- profile ------------------------------------------------------------

const MANUFACTURER_ID = '00000000-0000-0000-0000-000000000010';

export let MOCK_PROFILE = {
  id: MANUFACTURER_ID,
  userId: PRODUCER_USER_ID,
  companyName: 'ACME Textiles',
  description:
    'Family-owned weaving mill near İzmir. We supply organic cotton fabrics and finished apparel basics to mid-tier retailers across Europe and the Middle East.',
  country: 'TR',
  // Two PRODUCER kinds + one ECO_PRODUCER — drives auto eco flag in the picker.
  categories: [CAT.textiles, CAT.apparel, CAT.ecoTextiles],
  exportCountries: ['DE', 'GB', 'PL'],
  potentialMarkets: ['AE', 'IN', 'US', 'CA'],
  logo: link('media-logo-acme', { publicUrl: true, contentType: 'image/png' }),
  video: null,
  productionPhotos: links(['media-prod-floor-1', 'media-prod-floor-2'], {
    publicUrl: true,
    contentType: 'image/jpeg',
  }),
  certificates: links(['media-cert-iso9001', 'media-cert-gots'], {
    publicUrl: false,
    contentType: 'application/pdf',
  }),
  ecoCertificates: links(['media-cert-gots'], { publicUrl: true, contentType: 'application/pdf' }),
  active: true,
  ecoProducer: true,
  website: 'https://acme-textiles.example',
  foundedYear: 1998,
  employeesRange: 'M' as const,
  kycStatus: 'VERIFIED' as const,
  kycReason: null,
  createdAt: isoDaysAgo(420),
  updatedAt: isoDaysAgo(2),
};

export function setMockProfile(next: typeof MOCK_PROFILE): void {
  MOCK_PROFILE = next;
}

// ----- taxonomy -----------------------------------------------------------

function category(
  id: string,
  kind: CategoryKind,
  slug: string,
  sortOrder: number,
  name: { en: string; ru: string; uz: string },
): Category {
  return { id, kind, slug, parentId: null, sortOrder, name };
}

export const MOCK_CATEGORIES: Category[] = [
  // ── PRODUCER ────────────────────────────────────────────────────
  category(CAT.textiles, 'PRODUCER', 'textiles', 1, {
    en: 'Textiles & Fabrics',
    ru: 'Текстиль и ткани',
    uz: 'Toʻqimachilik',
  }),
  category(CAT.apparel, 'PRODUCER', 'apparel', 2, {
    en: 'Apparel & Garments',
    ru: 'Одежда',
    uz: 'Kiyim',
  }),
  category(CAT.food, 'PRODUCER', 'food', 3, {
    en: 'Food & Beverage',
    ru: 'Продукты питания',
    uz: 'Oziq-ovqat',
  }),
  category(CAT.electronics, 'PRODUCER', 'electronics', 4, {
    en: 'Electronics',
    ru: 'Электроника',
    uz: 'Elektronika',
  }),
  category(CAT.machinery, 'PRODUCER', 'machinery', 5, {
    en: 'Industrial Machinery',
    ru: 'Промышленное оборудование',
    uz: 'Sanoat uskunalari',
  }),
  category(CAT.chemicals, 'PRODUCER', 'chemicals', 6, {
    en: 'Chemicals',
    ru: 'Химия',
    uz: 'Kimyo',
  }),
  category(CAT.metals, 'PRODUCER', 'metals', 7, {
    en: 'Metals & Mining',
    ru: 'Металлургия',
    uz: 'Metallurgiya',
  }),
  category(CAT.homeware, 'PRODUCER', 'homeware', 8, {
    en: 'Home & Living',
    ru: 'Дом и интерьер',
    uz: 'Uy buyumlari',
  }),

  // ── ECO_PRODUCER ────────────────────────────────────────────────
  category(CAT.ecoTextiles, 'ECO_PRODUCER', 'eco-textiles', 9, {
    en: 'Organic & recycled textiles',
    ru: 'Эко-текстиль',
    uz: 'Eko-toʻqimachilik',
  }),
  category(CAT.ecoFood, 'ECO_PRODUCER', 'eco-food', 10, {
    en: 'Organic food',
    ru: 'Органические продукты',
    uz: 'Organik oziq-ovqat',
  }),
  category(CAT.ecoPackaging, 'ECO_PRODUCER', 'eco-packaging', 11, {
    en: 'Sustainable packaging',
    ru: 'Эко-упаковка',
    uz: 'Eko-qadoqlash',
  }),
  category(CAT.ecoEnergy, 'ECO_PRODUCER', 'eco-energy', 12, {
    en: 'Renewable energy hardware',
    ru: 'Возобновляемая энергетика',
    uz: 'Tiklanuvchi energiya',
  }),
  category(CAT.ecoCosmetics, 'ECO_PRODUCER', 'eco-cosmetics', 13, {
    en: 'Natural cosmetics',
    ru: 'Натуральная косметика',
    uz: 'Tabiiy kosmetika',
  }),

  // ── PRODUCT (marketplace) ──────────────────────────────────────
  category(CAT.productTextiles, 'PRODUCT', 'p-textiles', 14, {
    en: 'Textiles & Fabrics',
    ru: 'Текстиль и ткани',
    uz: 'Toʻqimachilik mahsulotlari',
  }),
  category(CAT.productApparel, 'PRODUCT', 'p-apparel', 15, {
    en: 'Apparel',
    ru: 'Одежда',
    uz: 'Kiyim',
  }),
  category(CAT.productHomeware, 'PRODUCT', 'p-homeware', 16, {
    en: 'Home goods',
    ru: 'Товары для дома',
    uz: 'Uy mahsulotlari',
  }),
  category(CAT.productElectronics, 'PRODUCT', 'p-electronics', 17, {
    en: 'Electronics',
    ru: 'Электроника',
    uz: 'Elektronika',
  }),
  category(CAT.productFood, 'PRODUCT', 'p-food', 18, {
    en: 'Food & Beverages',
    ru: 'Продукты питания',
    uz: 'Oziq-ovqat',
  }),
  category(CAT.productMachinery, 'PRODUCT', 'p-machinery', 19, {
    en: 'Industrial machinery',
    ru: 'Промышленное оборудование',
    uz: 'Sanoat uskunalari',
  }),
];

export const MOCK_HS_CODES = [
  { code: '5208', description: 'Woven fabrics of cotton, ≥85% cotton, ≤200 g/m²' },
  { code: '5209', description: 'Woven fabrics of cotton, ≥85% cotton, >200 g/m²' },
  { code: '6109', description: 'T-shirts, singlets and other vests, knitted or crocheted' },
  { code: '6302', description: 'Bed linen, table linen, toilet linen and kitchen linen' },
];

// ----- users (basic info) -------------------------------------------------

function buyer(id: string, displayName: string, initials: string, kyc: 'NONE' | 'VERIFIED' = 'NONE', active = true): UserBasicInfo {
  return { id, displayName, initials, roles: ['BUYER'], kycStatus: kyc, active };
}

const MOCK_USERS: Record<string, UserBasicInfo> = {
  [PRODUCER_USER_ID]: {
    id: PRODUCER_USER_ID,
    displayName: 'de**@a***.local',
    initials: 'DE',
    roles: ['PRODUCER'],
    kycStatus: 'VERIFIED',
    active: true,
  },
  [BUYER.germany]: buyer(BUYER.germany, 'lu***@be***.de', 'LB', 'VERIFIED'),
  [BUYER.uk]: buyer(BUYER.uk, 'ja***@mo***.co.uk', 'JM', 'VERIFIED'),
  [BUYER.usa]: buyer(BUYER.usa, 're***@no***.com', 'RN'),
  [BUYER.india]: buyer(BUYER.india, 'aj***@su***.in', 'AS', 'VERIFIED'),
  [BUYER.uae]: buyer(BUYER.uae, 'om***@gu***.ae', 'OG', 'NONE', false),
};

export function getUserBasic(id: string): UserBasicInfo {
  return (
    MOCK_USERS[id] ?? {
      id,
      displayName: `mo**@${id.slice(0, 4)}.local`,
      initials: id.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase() || '??',
      roles: ['BUYER'],
      kycStatus: 'NONE',
      active: true,
    }
  );
}

// ----- products -----------------------------------------------------------

export const MOCK_MANUFACTURER_BRIEF = {
  id: MANUFACTURER_ID,
  companyName: MOCK_PROFILE.companyName,
  country: MOCK_PROFILE.country,
  logo: MOCK_PROFILE.logo,
  ecoProducer: MOCK_PROFILE.ecoProducer,
  kycStatus: MOCK_PROFILE.kycStatus,
};

function product(partial: Partial<ProductView> & Pick<ProductView, 'id' | 'name' | 'categoryId' | 'exportPrice' | 'moq' | 'status'>): ProductView {
  return {
    manufacturer: MOCK_MANUFACTURER_BRIEF,
    hsCode: null,
    description: null,
    photos: links([`media-${partial.id}-1`, `media-${partial.id}-2`], { publicUrl: true, contentType: 'image/jpeg' }),
    originCountry: 'TR',
    eco: false,
    ecoCertificates: [],
    stock: false,
    stockDiscountPct: null,
    rejectionReason: null,
    createdAt: isoDaysAgo(60),
    updatedAt: isoDaysAgo(5),
    ...partial,
  };
}

export const MOCK_PRODUCTS: ProductView[] = [
  product({
    id: PROD.ecoCotton,
    name: {
      ru: 'Органическая хлопковая ткань 180 г/м² — сертификат GOTS',
      en: 'Organic cotton fabric 180 g/m² — GOTS certified',
      uz: 'Organik paxta mato 180 g/m² — GOTS sertifikati',
    },
    categoryId: CAT.productTextiles,
    hsCode: '5208',
    description: {
      ru: 'Органический хлопок с GOTS-сертификатом. Предусадка, OEKO-TEX класс I.',
      en: 'GOTS-certified organic cotton fabric. Pre-shrunk, OEKO-TEX class I.',
      uz: 'GOTS sertifikatiga ega organik paxta mato. Oldindan toraytirilgan, OEKO-TEX I sinf.',
    },
    exportPrice: { amount: '4.20', currency: 'USD' },
    moq: 1000,
    eco: true,
    ecoCertificates: links(['media-cert-gots'], { publicUrl: true, contentType: 'application/pdf' }),
    status: 'PUBLISHED',
    updatedAt: isoDaysAgo(3),
  }),
  product({
    id: PROD.recycledPoly,
    name: {
      ru: 'Флис из переработанного полиэстера 240 г/м²',
      en: 'Recycled polyester fleece 240 g/m²',
      uz: 'Qayta ishlangan poliester flis 240 g/m²',
    },
    categoryId: CAT.productTextiles,
    hsCode: '5408',
    description: {
      ru: 'Флис rPET, сотканный из 100% переработанных пост-потребительских бутылок.',
      en: 'rPET fleece woven from 100% post-consumer recycled bottles.',
      uz: '100% qayta ishlangan plastik shishalardan toʻqilgan rPET flis.',
    },
    exportPrice: { amount: '5.80', currency: 'USD' },
    moq: 500,
    eco: true,
    ecoCertificates: links(['media-cert-grs'], { publicUrl: true, contentType: 'application/pdf' }),
    status: 'PUBLISHED',
    updatedAt: isoDaysAgo(8),
  }),
  product({
    id: PROD.denimStock,
    name: {
      ru: 'Деним 12 oz индиго — сток-партия',
      en: 'Denim 12 oz indigo — stock lot',
      uz: 'Denim 12 oz indigo — stok partiyasi',
    },
    categoryId: CAT.productTextiles,
    hsCode: '5209',
    exportPrice: { amount: '6.40', currency: 'USD' },
    moq: 2000,
    stock: true,
    stockDiscountPct: 25,
    status: 'PUBLISHED',
    updatedAt: isoDaysAgo(12),
  }),
  product({
    id: PROD.draftBag,
    name: {
      ru: 'Парусиновая сумка-шопер — черновик',
      en: 'Canvas tote bag — draft',
      uz: 'Kanvas tote sumka — qoralama',
    },
    categoryId: CAT.productApparel,
    exportPrice: { amount: '2.10', currency: 'USD' },
    moq: 500,
    status: 'DRAFT',
    photos: links([], { publicUrl: true, contentType: 'image/jpeg' }),
    updatedAt: isoDaysAgo(1),
  }),
  product({
    id: PROD.pendingTowel,
    name: {
      ru: 'Турецкое банное полотенце 500 г/м² — на модерации',
      en: 'Turkish bath towel 500 g/m² — moderation',
      uz: 'Turk hammom sochiqi 500 g/m² — moderatsiyada',
    },
    categoryId: CAT.productHomeware,
    hsCode: '6302',
    exportPrice: { amount: '3.75', currency: 'USD' },
    moq: 200,
    status: 'PENDING_MODERATION',
    updatedAt: isoDaysAgo(2),
  }),
  product({
    id: PROD.rejectedShirt,
    name: {
      ru: 'Белая хлопковая футболка — отклонённый образец',
      en: 'White cotton t-shirt — rejected sample',
      uz: 'Oq paxta futbolka — rad etilgan namuna',
    },
    categoryId: CAT.productApparel,
    hsCode: '6109',
    exportPrice: { amount: '1.95', currency: 'USD' },
    moq: 1000,
    status: 'REJECTED',
    rejectionReason: 'Photo quality is too low — please re-upload at ≥1024×1024.',
    updatedAt: isoDaysAgo(4),
  }),
];

// ----- buy requests -------------------------------------------------------

function buyRequest(p: Partial<BuyRequest> & Pick<BuyRequest, 'id' | 'buyerId' | 'name' | 'category' | 'status'>): BuyRequest {
  return {
    hsCode: null,
    volumeText: null,
    budget: null,
    deadline: null,
    destinationCountry: null,
    techDescription: null,
attachments: links([], { publicUrl: false, contentType: 'application/pdf' }),
    dispatch: 'PUBLIC',
    targetManufacturer: null,
    hiddenByAdmin: false,
    hiddenReason: null,
    quoteCount: 0,
    createdAt: isoDaysAgo(7),
    updatedAt: isoDaysAgo(3),
    alreadyQuoted: false,
    ...p,
  };
}

export const MOCK_BUY_REQUESTS: BuyRequest[] = [
  buyRequest({
    id: RFQ.publicCotton,
    buyerId: BUYER.germany,
    name: 'Organic cotton fabric 200 g/m² — 8000 m²/mo',
    category: CAT.productTextiles,
    hsCode: '5208',
    volumeText: '8000 m²/month',
    budget: { amount: '32000.00', currency: 'EUR' },
    deadline: isoDaysAgo(-21).slice(0, 10),
    destinationCountry: 'DE',
    techDescription: 'Organic cotton, GOTS preferred. 200 g/m², width 150 cm, natural undyed.',
attachments: links(['media-spec-cotton'], { publicUrl: false, contentType: 'application/pdf' }),
    quoteCount: 3,
    status: 'OPEN',
    createdAt: isoDaysAgo(2),
    updatedAt: isoDaysAgo(1),
  }),
  buyRequest({
    id: RFQ.publicLinen,
    buyerId: BUYER.uk,
    name: 'Linen blend shirts — recurring 5000 pcs/qtr',
    category: CAT.productApparel,
    hsCode: '6206',
    volumeText: '5000 pcs/quarter',
    budget: { amount: '60000.00', currency: 'GBP' },
    deadline: isoDaysAgo(-45).slice(0, 10),
    destinationCountry: 'GB',
    techDescription: '55/45 linen-cotton blend, button-down, sizes XS-XXL distribution per attached size curve.',
attachments: links(['media-spec-linen', 'media-spec-sizes'], { publicUrl: false, contentType: 'application/pdf' }),
    quoteCount: 7,
    status: 'OPEN',
    alreadyQuoted: true,
    createdAt: isoDaysAgo(9),
    updatedAt: isoDaysAgo(2),
  }),
  buyRequest({
    id: RFQ.publicHomeware,
    buyerId: BUYER.india,
    name: 'Hotel bedlinen set — 12 000 sets',
    category: CAT.productHomeware,
    hsCode: '6302',
    volumeText: '12 000 sets one-time',
    budget: { amount: '180000.00', currency: 'USD' },
    deadline: isoDaysAgo(-60).slice(0, 10),
    destinationCountry: 'IN',
    techDescription: 'Hotel-grade percale, 300 thread count, white. Set: 1 fitted, 1 flat, 2 pillowcases.',
attachments: links([], { publicUrl: false, contentType: 'application/pdf' }),
    quoteCount: 11,
    status: 'OPEN',
    alreadyQuoted: true,
    createdAt: isoDaysAgo(15),
    updatedAt: isoDaysAgo(4),
  }),
  buyRequest({
    id: RFQ.targetedDenim,
    buyerId: BUYER.usa,
    name: 'Denim 12 oz indigo — 50 000 m, targeted',
    category: CAT.productTextiles,
    hsCode: '5209',
    volumeText: '50 000 m one-time',
    budget: { amount: '275000.00', currency: 'USD' },
    deadline: isoDaysAgo(-30).slice(0, 10),
    destinationCountry: 'US',
    techDescription: 'Targeted procurement based on your denim stock listing. Indigo dye, selvage edge preferred.',
attachments: links(['media-spec-denim'], { publicUrl: false, contentType: 'application/pdf' }),
    dispatch: 'TARGETED',
    targetManufacturer: MANUFACTURER_ID,
    quoteCount: 1,
    status: 'OPEN',
    createdAt: isoDaysAgo(1),
    updatedAt: isoDaysAgo(1),
  }),
  buyRequest({
    id: RFQ.closedShirts,
    buyerId: BUYER.uae,
    name: 'Workwear shirts — closed (awarded)',
    category: CAT.productApparel,
    volumeText: '3000 pcs',
    budget: { amount: '21000.00', currency: 'USD' },
    destinationCountry: 'AE',
    techDescription: 'Industrial workwear, indigo or grey. Cycle awarded to another supplier.',
    quoteCount: 6,
    status: 'CLOSED',
    createdAt: isoDaysAgo(25),
    updatedAt: isoDaysAgo(8),
  }),
  buyRequest({
    id: RFQ.expiredTowels,
    buyerId: BUYER.uk,
    name: 'Spa towel set — deadline passed',
    category: CAT.productHomeware,
    volumeText: '2000 sets',
    budget: { amount: '14000.00', currency: 'GBP' },
    destinationCountry: 'GB',
    techDescription: 'Bamboo-cotton blend bath set. Deadline expired before sourcing decision.',
    quoteCount: 2,
    status: 'EXPIRED',
    createdAt: isoDaysAgo(50),
    updatedAt: isoDaysAgo(15),
  }),
];

export function getBuyRequest(id: string): BuyRequest | null {
  return MOCK_BUY_REQUESTS.find((r) => r.id === id) ?? null;
}

// ----- quotes -------------------------------------------------------------

function quote(p: Partial<Quote> & Pick<Quote, 'id' | 'buyRequestId' | 'priceTotal' | 'leadTimeDays'>): Quote {
  return {
    manufacturerId: MANUFACTURER_ID,
    terms: null,
attachments: links([], { publicUrl: false, contentType: 'application/pdf' }),
    acceptedAt: null,
    withdrawn: false,
    createdAt: isoDaysAgo(5),
    updatedAt: isoDaysAgo(5),
    ...p,
  };
}

export const MOCK_QUOTES: Quote[] = [
  quote({
    id: QUOTE.activeCotton,
    buyRequestId: RFQ.publicCotton,
    buyRequestTitle: 'Organic cotton fabric 200 g/m²',
    priceTotal: { amount: '30800.00', currency: 'EUR' },
    leadTimeDays: 28,
    terms: 'EXW İzmir, 50% deposit, balance against B/L. Sample lead time 7 days.',
    createdAt: isoDaysAgo(2),
    updatedAt: isoDaysAgo(2),
  }),
  quote({
    id: QUOTE.activeDenim,
    buyRequestId: RFQ.targetedDenim,
    buyRequestTitle: 'Denim 12 oz indigo — targeted',
    priceTotal: { amount: '265000.00', currency: 'USD' },
    leadTimeDays: 45,
    terms: 'FOB İzmir. Includes lab dips and pre-production sampling.',
    createdAt: isoDaysAgo(1),
    updatedAt: isoDaysAgo(1),
  }),
  quote({
    id: QUOTE.acceptedHomeware,
    buyRequestId: RFQ.publicHomeware,
    buyRequestTitle: 'Hotel bedlinen set — 12 000 sets',
    priceTotal: { amount: '172000.00', currency: 'USD' },
    leadTimeDays: 60,
    terms: 'CIF Mumbai. Includes 200 pre-production samples.',
    acceptedAt: isoDaysAgo(4),
    createdAt: isoDaysAgo(8),
    updatedAt: isoDaysAgo(4),
  }),
  quote({
    id: QUOTE.withdrawnLinen,
    buyRequestId: RFQ.publicLinen,
    buyRequestTitle: 'Linen blend shirts',
    priceTotal: { amount: '63000.00', currency: 'GBP' },
    leadTimeDays: 50,
    withdrawn: true,
    terms: 'Withdrawn — capacity reallocated to a sister project.',
    createdAt: isoDaysAgo(10),
    updatedAt: isoDaysAgo(6),
  }),
  quote({
    id: QUOTE.activeLinen2,
    buyRequestId: RFQ.publicLinen,
    buyRequestTitle: 'Linen blend shirts (resubmitted)',
    priceTotal: { amount: '58500.00', currency: 'GBP' },
    leadTimeDays: 35,
    terms: 'Revised offer with reduced lead time.',
    createdAt: isoDaysAgo(2),
    updatedAt: isoDaysAgo(2),
  }),
];

// ----- orders -------------------------------------------------------------

function order(p: Partial<OrderView> & Pick<OrderView, 'id' | 'title' | 'buyerId' | 'totalAmount' | 'status'>): OrderView {
  return {
    producerId: MANUFACTURER_ID,
    producer: MOCK_MANUFACTURER_BRIEF,
    buyer: getUserBasic(p.buyerId),
    buyRequestId: null,
    quoteId: null,
    leadTimeDays: null,
    documents: links([], { publicUrl: false, contentType: 'application/pdf' }),
    history: [],
    createdAt: isoDaysAgo(10),
    updatedAt: isoDaysAgo(2),
    ...p,
  };
}

export const MOCK_ORDERS: OrderView[] = [
  order({
    id: ORDER.newShirts,
    title: 'PO-2026-0041 · Linen shirts pilot',
    buyerId: BUYER.uk,
    totalAmount: { amount: '12480.00', currency: 'GBP' },
    leadTimeDays: 21,
    quoteId: QUOTE.activeLinen2,
    buyRequestId: RFQ.publicLinen,
    status: 'NEW',
documents: links(['media-doc-po-0041'], { publicUrl: false, contentType: 'application/pdf' }),
    history: [
      { from: null, to: 'NEW', actor: BUYER.uk, at: isoDaysAgo(1), note: 'Quote accepted — order created automatically.' },
    ],
    createdAt: isoDaysAgo(1),
    updatedAt: isoDaysAgo(1),
  }),
  order({
    id: ORDER.inProcessing,
    title: 'PO-2026-0038 · Organic cotton roll',
    buyerId: BUYER.germany,
    totalAmount: { amount: '30800.00', currency: 'EUR' },
    leadTimeDays: 28,
    quoteId: QUOTE.activeCotton,
    buyRequestId: RFQ.publicCotton,
    status: 'IN_PROCESSING',
documents: links(['media-doc-po-0038', 'media-doc-proforma-0038'], { publicUrl: false, contentType: 'application/pdf' }),
    history: [
      { from: null, to: 'NEW', actor: BUYER.germany, at: isoDaysAgo(7), note: null },
      { from: 'NEW', to: 'IN_PROCESSING', actor: PRODUCER_USER_ID, at: isoDaysAgo(5), note: 'Yarn allocated, dyeing scheduled.' },
    ],
    createdAt: isoDaysAgo(7),
    updatedAt: isoDaysAgo(5),
  }),
  order({
    id: ORDER.readyToShip,
    title: 'PO-2026-0032 · Hotel bedlinen batch 1',
    buyerId: BUYER.india,
    totalAmount: { amount: '172000.00', currency: 'USD' },
    leadTimeDays: 60,
    quoteId: QUOTE.acceptedHomeware,
    buyRequestId: RFQ.publicHomeware,
    status: 'READY_TO_SHIP',
documents: links(['media-doc-po-0032', 'media-doc-packing-list-0032'], { publicUrl: false, contentType: 'application/pdf' }),
    history: [
      { from: null, to: 'NEW', actor: BUYER.india, at: isoDaysAgo(40), note: null },
      { from: 'NEW', to: 'IN_PROCESSING', actor: PRODUCER_USER_ID, at: isoDaysAgo(35), note: null },
      { from: 'IN_PROCESSING', to: 'READY_TO_SHIP', actor: PRODUCER_USER_ID, at: isoDaysAgo(1), note: 'Loaded into container MSCU1234567.' },
    ],
    createdAt: isoDaysAgo(40),
    updatedAt: isoDaysAgo(1),
  }),
  order({
    id: ORDER.inTransit,
    title: 'PO-2026-0029 · Denim rolls — pilot',
    buyerId: BUYER.usa,
    totalAmount: { amount: '49500.00', currency: 'USD' },
    leadTimeDays: 30,
    status: 'IN_TRANSIT',
documents: links(['media-doc-po-0029', 'media-doc-bl-0029'], { publicUrl: false, contentType: 'application/pdf' }),
    history: [
      { from: null, to: 'NEW', actor: BUYER.usa, at: isoDaysAgo(45), note: null },
      { from: 'NEW', to: 'IN_PROCESSING', actor: PRODUCER_USER_ID, at: isoDaysAgo(40), note: null },
      { from: 'IN_PROCESSING', to: 'READY_TO_SHIP', actor: PRODUCER_USER_ID, at: isoDaysAgo(12), note: null },
      { from: 'READY_TO_SHIP', to: 'IN_TRANSIT', actor: PRODUCER_USER_ID, at: isoDaysAgo(8), note: 'Vessel ETA Long Beach 2026-07-08.' },
    ],
    createdAt: isoDaysAgo(45),
    updatedAt: isoDaysAgo(8),
  }),
  order({
    id: ORDER.delivered,
    title: 'PO-2026-0014 · Recycled polyester fleece',
    buyerId: BUYER.germany,
    totalAmount: { amount: '24200.00', currency: 'EUR' },
    leadTimeDays: 35,
    status: 'DELIVERED',
documents: links(['media-doc-po-0014', 'media-doc-pod-0014'], { publicUrl: false, contentType: 'application/pdf' }),
    history: [
      { from: null, to: 'NEW', actor: BUYER.germany, at: isoDaysAgo(80), note: null },
      { from: 'NEW', to: 'IN_PROCESSING', actor: PRODUCER_USER_ID, at: isoDaysAgo(72), note: null },
      { from: 'IN_PROCESSING', to: 'READY_TO_SHIP', actor: PRODUCER_USER_ID, at: isoDaysAgo(40), note: null },
      { from: 'READY_TO_SHIP', to: 'IN_TRANSIT', actor: PRODUCER_USER_ID, at: isoDaysAgo(35), note: null },
      { from: 'IN_TRANSIT', to: 'AT_CUSTOMS', actor: PRODUCER_USER_ID, at: isoDaysAgo(20), note: null },
      { from: 'AT_CUSTOMS', to: 'DELIVERED', actor: PRODUCER_USER_ID, at: isoDaysAgo(15), note: 'Signed POD attached.' },
    ],
    createdAt: isoDaysAgo(80),
    updatedAt: isoDaysAgo(15),
  }),
];

export function getOrder(id: string): OrderView | null {
  return MOCK_ORDERS.find((o) => o.id === id) ?? null;
}

export function updateOrderStatus(id: string, status: OrderView['status'], note: string | null): OrderView | null {
  const idx = MOCK_ORDERS.findIndex((o) => o.id === id);
  if (idx < 0) return null;
  const o = MOCK_ORDERS[idx]!;
  const updated: OrderView = {
    ...o,
    status,
    updatedAt: new Date().toISOString(),
    history: [
      ...o.history,
      { from: o.status, to: status, actor: PRODUCER_USER_ID, at: new Date().toISOString(), note },
    ],
  };
  MOCK_ORDERS[idx] = updated;
  return updated;
}

// ----- logistics ----------------------------------------------------------

export const MOCK_CARRIERS: Carrier[] = [
  { id: CARRIER_IDS.dhl, name: 'DHL Global Forwarding', active: true },
  { id: CARRIER_IDS.fedex, name: 'FedEx Trade Networks', active: true },
  { id: CARRIER_IDS.maersk, name: 'Maersk Ocean', active: true },
];

export const MOCK_SHIPMENTS: Record<string, ShipmentView> = {
  [ORDER.inTransit]: {
    id: 'shipment-' + ORDER.inTransit,
    orderId: ORDER.inTransit,
    carrier: MOCK_CARRIERS[2]!,
    trackingNumber: 'MSCU1234567',
    etaDate: isoDaysAgo(-10).slice(0, 10),
    status: 'IN_TRANSIT',
    history: [
      { status: 'BOOKED', at: isoDaysAgo(15), note: 'Booking confirmed by Maersk.' },
      { status: 'LOADED', at: isoDaysAgo(10), note: 'Container loaded at İzmir.' },
      { status: 'IN_TRANSIT', at: isoDaysAgo(8), note: 'Vessel departed İzmir → Long Beach.' },
    ],
    createdAt: isoDaysAgo(15),
    updatedAt: isoDaysAgo(8),
  },
  [ORDER.readyToShip]: {
    id: 'shipment-' + ORDER.readyToShip,
    orderId: ORDER.readyToShip,
    carrier: null,
    trackingNumber: null,
    etaDate: null,
    status: 'READY_TO_SHIP',
    history: [],
    createdAt: isoDaysAgo(2),
    updatedAt: isoDaysAgo(1),
  },
  [ORDER.delivered]: {
    id: 'shipment-' + ORDER.delivered,
    orderId: ORDER.delivered,
    carrier: MOCK_CARRIERS[0]!,
    trackingNumber: 'DHL4421887651',
    etaDate: isoDaysAgo(15).slice(0, 10),
    status: 'DELIVERED',
    history: [
      { status: 'BOOKED', at: isoDaysAgo(40), note: null },
      { status: 'IN_TRANSIT', at: isoDaysAgo(35), note: null },
      { status: 'DELIVERED', at: isoDaysAgo(15), note: 'Signed POD.' },
    ],
    createdAt: isoDaysAgo(40),
    updatedAt: isoDaysAgo(15),
  },
};

export function getShipment(orderId: string): ShipmentView | null {
  return MOCK_SHIPMENTS[orderId] ?? null;
}

// ----- CRM leads ----------------------------------------------------------

function lead(p: Partial<Lead> & Pick<Lead, 'id' | 'title' | 'status' | 'source'>): Lead {
  return {
    manufacturerId: MANUFACTURER_ID,
    buyerId: null,
    notes: null,
    sourceRefId: null,
    createdAt: isoDaysAgo(15),
    updatedAt: isoDaysAgo(3),
    ...p,
  };
}

export const MOCK_LEADS: Lead[] = [
  lead({
    id: 'lead-001',
    title: 'Targeted denim RFQ — Northbound (USA)',
    buyerId: BUYER.usa,
    sourceRefId: RFQ.targetedDenim,
    notes: 'Hot — they explicitly asked for our denim. Schedule call this week.',
    status: 'NEW',
    source: 'RFQ_TARGET',
    createdAt: isoDaysAgo(1),
    updatedAt: isoDaysAgo(1),
  }),
  lead({
    id: 'lead-002',
    title: 'Hotel bedlinen — Subcontinent Hospitality (India)',
    buyerId: BUYER.india,
    sourceRefId: ORDER.readyToShip,
    notes: 'Repeat customer signal — 2nd order in 6 months.',
    status: 'WON',
    source: 'ORDER',
  }),
  lead({
    id: 'lead-003',
    title: 'Organic cotton roll — Berliner Textil (DE)',
    buyerId: BUYER.germany,
    sourceRefId: ORDER.inProcessing,
    status: 'WON',
    source: 'ORDER',
  }),
  lead({
    id: 'lead-004',
    title: 'Linen shirts pilot — Mossfield (UK)',
    buyerId: BUYER.uk,
    sourceRefId: ORDER.newShirts,
    notes: 'Pilot 5000 → potential 50k/year contract.',
    status: 'WON',
    source: 'ORDER',
  }),
  lead({
    id: 'lead-005',
    title: 'Linen blend shirts — Mossfield re-quote',
    buyerId: BUYER.uk,
    sourceRefId: QUOTE.activeLinen2,
    notes: 'Resubmitted after withdrawing first offer.',
    status: 'QUOTED',
    source: 'QUOTE',
  }),
  lead({
    id: 'lead-006',
    title: 'Workwear — Gulf Supplies (UAE)',
    buyerId: BUYER.uae,
    sourceRefId: RFQ.closedShirts,
    notes: 'Lost to local supplier — cost was 18% lower.',
    status: 'LOST',
    source: 'RFQ_TARGET',
  }),
  lead({
    id: 'lead-007',
    title: 'Recycled polyester — Berliner Textil',
    buyerId: BUYER.germany,
    sourceRefId: ORDER.delivered,
    status: 'WON',
    source: 'ORDER',
  }),
  lead({
    id: 'lead-008',
    title: 'Cotton fabric — Berliner Textil active quote',
    buyerId: BUYER.germany,
    sourceRefId: QUOTE.activeCotton,
    notes: 'Awaiting response on counter-offer.',
    status: 'IN_TOUCH',
    source: 'QUOTE',
  }),
  lead({
    id: 'lead-009',
    title: 'Bath towels — Spa Group',
    buyerId: BUYER.uk,
    sourceRefId: RFQ.expiredTowels,
    notes: 'Expired — circle back next quarter.',
    status: 'LOST',
    source: 'RFQ_TARGET',
  }),
  lead({
    id: 'lead-010',
    title: 'New customer outreach — Asia mailing list',
    notes: 'Manual entry from trade show roster.',
    status: 'NEW',
    source: 'MANUAL',
    createdAt: isoDaysAgo(3),
    updatedAt: isoDaysAgo(3),
  }),
];

export function getLead(id: string): Lead | null {
  return MOCK_LEADS.find((l) => l.id === id) ?? null;
}

export function patchLead(id: string, patch: Partial<Lead>): Lead | null {
  const idx = MOCK_LEADS.findIndex((l) => l.id === id);
  if (idx < 0) return null;
  const updated = { ...MOCK_LEADS[idx]!, ...patch, updatedAt: new Date().toISOString() };
  MOCK_LEADS[idx] = updated;
  return updated;
}

// ----- conversations / messages ------------------------------------------

function conv(
  id: string,
  buyerId: string,
  extras: Partial<ConversationView>,
): ConversationView {
  return {
    id,
    buyerId,
    producerId: MANUFACTURER_ID,
    buyer: getUserBasic(buyerId),
    producer: MOCK_MANUFACTURER_BRIEF,
    unreadCount: 0,
    lastMessagePreview: null,
    lastMessageSenderId: null,
    lastMessageHasAttachments: false,
    lastMessageAt: null,
    counterpartOnline: false,
    ...extras,
  };
}

export const MOCK_CONVERSATIONS: ConversationView[] = [
  conv(CONV.germany, BUYER.germany, {
    unreadCount: 0,
    lastMessagePreview: 'Great, please send the invoice for the deposit.',
    lastMessageSenderId: BUYER.germany,
    lastMessageAt: isoDaysAgo(1),
    counterpartOnline: true,
  }),
  conv(CONV.uk, BUYER.uk, {
    unreadCount: 2,
    lastMessagePreview: 'Can you confirm pre-production sample timing?',
    lastMessageSenderId: BUYER.uk,
    lastMessageAt: isoDaysAgo(0.5),
  }),
  conv(CONV.india, BUYER.india, {
    lastMessagePreview: 'Container loaded — sending B/L tomorrow.',
    lastMessageSenderId: PRODUCER_USER_ID,
    lastMessageHasAttachments: true,
    lastMessageAt: isoDaysAgo(1),
  }),
  conv(CONV.uae, BUYER.uae, {}),
];

function msg(
  conversationId: string,
  partial: { id: string; senderId: string; body: string; daysAgo: number; status?: 'SENT' | 'DELIVERED' | 'READ' },
): MessageView {
  return {
    id: partial.id,
    conversationId,
    senderId: partial.senderId,
    body: partial.body,
attachments: links([], { publicUrl: false, contentType: 'application/pdf' }),
    status: partial.status ?? 'READ',
    createdAt: isoDaysAgo(partial.daysAgo),
  };
}

const MESSAGES: Record<string, MessageView[]> = {
  [CONV.germany]: [
    msg(CONV.germany, { id: 'm-de-1', senderId: BUYER.germany, body: 'Hi! We are interested in your organic cotton roll. Can you ship to Hamburg?', daysAgo: 7 }),
    msg(CONV.germany, { id: 'm-de-2', senderId: PRODUCER_USER_ID, body: 'Hi Luca — yes, regular EXW/CIF service to Hamburg. What MOQ are you targeting?', daysAgo: 7 }),
    msg(CONV.germany, { id: 'm-de-3', senderId: BUYER.germany, body: 'Initially 8000 m². Need it before the AW collection sampling round.', daysAgo: 6 }),
    msg(CONV.germany, { id: 'm-de-4', senderId: PRODUCER_USER_ID, body: 'Got it. Sent a formal quote — 30 800 EUR, 28-day lead time. Review and let me know.', daysAgo: 5 }),
    msg(CONV.germany, { id: 'm-de-5', senderId: BUYER.germany, body: 'Quote accepted internally. PO drafted.', daysAgo: 4 }),
    msg(CONV.germany, { id: 'm-de-6', senderId: BUYER.germany, body: 'Great, please send the invoice for the deposit.', daysAgo: 1, status: 'READ' }),
  ],
  [CONV.uk]: [
    msg(CONV.uk, { id: 'm-uk-1', senderId: BUYER.uk, body: 'Hello! Looking at your t-shirt and linen catalogue.', daysAgo: 10 }),
    msg(CONV.uk, { id: 'm-uk-2', senderId: PRODUCER_USER_ID, body: 'Hi James! Happy to send fabric swatches and a sample size run.', daysAgo: 10 }),
    msg(CONV.uk, { id: 'm-uk-3', senderId: BUYER.uk, body: 'Swatches received, fabric looks great. Sending PO for the pilot.', daysAgo: 4 }),
    msg(CONV.uk, { id: 'm-uk-4', senderId: PRODUCER_USER_ID, body: 'Confirmed. We will begin pre-production in 5 business days.', daysAgo: 3 }),
    msg(CONV.uk, { id: 'm-uk-5', senderId: BUYER.uk, body: 'Can you confirm pre-production sample timing?', daysAgo: 0.5, status: 'DELIVERED' }),
    msg(CONV.uk, { id: 'm-uk-6', senderId: BUYER.uk, body: 'Also — what is the warranty on the labels?', daysAgo: 0.5, status: 'DELIVERED' }),
  ],
  [CONV.india]: [
    msg(CONV.india, { id: 'm-in-1', senderId: BUYER.india, body: 'Production update?', daysAgo: 2 }),
    msg(CONV.india, { id: 'm-in-2', senderId: PRODUCER_USER_ID, body: 'On schedule. Loading planned tomorrow.', daysAgo: 1.8 }),
    msg(CONV.india, { id: 'm-in-3', senderId: PRODUCER_USER_ID, body: 'Container loaded — sending B/L tomorrow.', daysAgo: 1 }),
  ],
  [CONV.uae]: [],
};

export function getConversation(id: string): ConversationView | null {
  return MOCK_CONVERSATIONS.find((c) => c.id === id) ?? null;
}

export function getMessages(conversationId: string): MessageView[] {
  return MESSAGES[conversationId] ?? [];
}

export function appendMessage(
  conversationId: string,
  body: string,
  attachments: string[],
): MessageView | null {
  const conv = getConversation(conversationId);
  if (!conv) return null;
  const list = MESSAGES[conversationId] ?? [];
  // The form sends UUIDs; backend resolves them into MediaLink[] for the
  // response — emulate that here so the chat UI renders previews correctly.
  const message: MessageView = {
    id: `m-${conversationId}-${list.length + 1}`,
    conversationId,
    senderId: PRODUCER_USER_ID,
    body,
    attachments: links(attachments, { publicUrl: false, contentType: 'application/pdf' }),
    status: 'SENT',
    createdAt: new Date().toISOString(),
  };
  MESSAGES[conversationId] = [...list, message];
  conv.lastMessagePreview = body.slice(0, 80);
  conv.lastMessageAt = message.createdAt;
  return message;
}

// ----- notifications ------------------------------------------------------

function notif(p: Partial<NotificationView> & Pick<NotificationView, 'id' | 'type' | 'title' | 'message'>): NotificationView {
  return {
    payload: null,
    read: false,
    createdAt: isoDaysAgo(2),
    ...p,
  };
}

export const MOCK_NOTIFICATIONS: NotificationView[] = [
  notif({
    id: 'n-001',
    type: 'RFQ_RECEIVED',
    title: 'Targeted request from Northbound (USA)',
    message: '50 000 m of indigo denim — based on your stock listing.',
    payload: { buyRequestId: RFQ.targetedDenim },
    createdAt: isoDaysAgo(1),
  }),
  notif({
    id: 'n-002',
    type: 'QUOTE_ACCEPTED',
    title: 'Quote accepted — hotel bedlinen',
    message: 'Subcontinent Hospitality accepted your quote of 172 000 USD. Order PO-2026-0032 created.',
    payload: { orderId: ORDER.readyToShip, quoteId: QUOTE.acceptedHomeware },
    createdAt: isoDaysAgo(4),
    read: true,
  }),
  notif({
    id: 'n-003',
    type: 'ORDER_STATUS_CHANGED',
    title: 'Order PO-2026-0029 → IN_TRANSIT',
    message: 'Vessel ETA Long Beach 2026-07-08.',
    payload: { orderId: ORDER.inTransit },
    createdAt: isoDaysAgo(8),
    read: true,
  }),
  notif({
    id: 'n-004',
    type: 'CHAT_MESSAGE',
    title: 'Mossfield (UK) sent 2 messages',
    message: 'Can you confirm pre-production sample timing?',
    payload: { conversationId: CONV.uk },
    createdAt: isoDaysAgo(0.5),
  }),
  notif({
    id: 'n-005',
    type: 'CHAT_MESSAGE',
    title: 'Berliner Textil — deposit invoice request',
    message: 'Great, please send the invoice for the deposit.',
    payload: { conversationId: CONV.germany },
    createdAt: isoDaysAgo(1),
    read: true,
  }),
  notif({
    id: 'n-006',
    type: 'KYC_APPROVED',
    title: 'Verified Supplier status granted',
    message: 'You can now publish products and respond to RFQs.',
    createdAt: isoDaysAgo(30),
    read: true,
  }),
  notif({
    id: 'n-007',
    type: 'SYSTEM',
    title: 'New analytics features available',
    message: 'Funnel and conversion breakdowns are on the dashboard.',
    createdAt: isoDaysAgo(12),
    read: true,
  }),
  notif({
    id: 'n-008',
    type: 'RFQ_RECEIVED',
    title: 'New public request: linen blend shirts',
    message: 'Mossfield (UK) is sourcing 5000 pcs/qtr.',
    payload: { buyRequestId: RFQ.publicLinen },
    createdAt: isoDaysAgo(9),
    read: true,
  }),
];

export function getUnreadNotificationsCount(): number {
  return MOCK_NOTIFICATIONS.filter((n) => !n.read).length;
}

export function markNotificationRead(id: string): void {
  const n = MOCK_NOTIFICATIONS.find((it) => it.id === id);
  if (n) n.read = true;
}

export function markAllNotificationsRead(): number {
  let count = 0;
  for (const n of MOCK_NOTIFICATIONS) {
    if (!n.read) {
      n.read = true;
      count++;
    }
  }
  return count;
}

// ----- analytics ----------------------------------------------------------

const DAILY: { d: number; PROFILE_VIEW: number; PRODUCT_VIEW: number; REQUEST_RECEIVED: number; CHAT_STARTED: number; DEAL_WON: number }[] = [
  { d: 6, PROFILE_VIEW: 28, PRODUCT_VIEW: 91, REQUEST_RECEIVED: 1, CHAT_STARTED: 2, DEAL_WON: 0 },
  { d: 5, PROFILE_VIEW: 34, PRODUCT_VIEW: 112, REQUEST_RECEIVED: 2, CHAT_STARTED: 3, DEAL_WON: 1 },
  { d: 4, PROFILE_VIEW: 41, PRODUCT_VIEW: 138, REQUEST_RECEIVED: 3, CHAT_STARTED: 1, DEAL_WON: 0 },
  { d: 3, PROFILE_VIEW: 52, PRODUCT_VIEW: 155, REQUEST_RECEIVED: 4, CHAT_STARTED: 4, DEAL_WON: 1 },
  { d: 2, PROFILE_VIEW: 48, PRODUCT_VIEW: 142, REQUEST_RECEIVED: 2, CHAT_STARTED: 3, DEAL_WON: 0 },
  { d: 1, PROFILE_VIEW: 61, PRODUCT_VIEW: 178, REQUEST_RECEIVED: 3, CHAT_STARTED: 5, DEAL_WON: 1 },
  { d: 0, PROFILE_VIEW: 55, PRODUCT_VIEW: 169, REQUEST_RECEIVED: 3, CHAT_STARTED: 6, DEAL_WON: 1 },
];

const SUM = {
  PROFILE_VIEW: DAILY.reduce((s, p) => s + p.PROFILE_VIEW, 0),
  PRODUCT_VIEW: DAILY.reduce((s, p) => s + p.PRODUCT_VIEW, 0),
  REQUEST_RECEIVED: DAILY.reduce((s, p) => s + p.REQUEST_RECEIVED, 0),
  CHAT_STARTED: DAILY.reduce((s, p) => s + p.CHAT_STARTED, 0),
  DEAL_WON: DAILY.reduce((s, p) => s + p.DEAL_WON, 0),
};
const FUNNEL_VIEWS = SUM.PROFILE_VIEW + SUM.PRODUCT_VIEW;
const FUNNEL_CHATS = SUM.CHAT_STARTED;
const FUNNEL_DEALS = SUM.DEAL_WON;

export const MOCK_ANALYTICS = {
  range: 'WEEK',
  from: isoDaysAgo(6).slice(0, 10),
  to: isoDaysAgo(0).slice(0, 10),
  totals: SUM,
  daily: DAILY.map((p) => ({
    day: isoDaysAgo(p.d).slice(0, 10),
    counts: {
      PROFILE_VIEW: p.PROFILE_VIEW,
      PRODUCT_VIEW: p.PRODUCT_VIEW,
      REQUEST_RECEIVED: p.REQUEST_RECEIVED,
      CHAT_STARTED: p.CHAT_STARTED,
      DEAL_WON: p.DEAL_WON,
    },
  })),
  funnel: {
    views: FUNNEL_VIEWS,
    chats: FUNNEL_CHATS,
    deals: FUNNEL_DEALS,
    viewsToChats: FUNNEL_VIEWS ? FUNNEL_CHATS / FUNNEL_VIEWS : 0,
    chatsToDeals: FUNNEL_CHATS ? FUNNEL_DEALS / FUNNEL_CHATS : 0,
    overall: FUNNEL_VIEWS ? FUNNEL_DEALS / FUNNEL_VIEWS : 0,
  },
};

// ----- pagination helper --------------------------------------------------

export function paginate<T>(items: T[], page = 0, size = 20) {
  const start = page * size;
  const slice = items.slice(start, start + size);
  return {
    items: slice,
    page,
    size,
    total: items.length,
    totalPages: Math.max(1, Math.ceil(items.length / size)),
  };
}

export const EMPTY_PAGE = { items: [], page: 0, size: 20, total: 0, totalPages: 0 };
